import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { BRAND, type Brand } from '../brand';

/* Blog data layer — two independent sources:

   `blog/current`  = { posts: BlogPost[] }   — YOUR articles, written in /admin/blog.
                     This is the main /blog feed and the only content we author.

   `news/auto`     = { consulting: [], accounting: [], updatedAt } — industry headlines
                     fetched hourly by the backend `refreshNews` scheduled function.
                     Link-out only: we show the headline + source and send the reader to
                     the publisher. We never republish article bodies (licensing).

   Deliberately NOT `news/current` — that doc is the admin-written News & Advisory feed
   and is left untouched by the fetcher. */

export type BlogPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;          // plain text / simple markdown-ish; rendered as paragraphs
  category: string;
  image: string;
  author: string;
  readTime: string;      // e.g. "5 min read"
  publishedAt: string;   // ISO date
  published: boolean;
  brand: Brand | 'both'; // which site shows it
  // Set when the draft was generated from an industry headline in /admin/blog.
  // Kept so the article page can credit and link back to the publisher — we
  // summarise and comment, we never republish the original body.
  sourceUrl?: string;
  sourceName?: string;
};

export type AutoNewsItem = {
  id: string;
  title: string;
  source: string;
  url: string;
  publishedAt: string;
  image: string;
  tag: string;
};

/** The `news/auto` key for the current brand. */
export const NEWS_VIEW: 'consulting' | 'accounting' = BRAND === 'corporate' ? 'accounting' : 'consulting';

export const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'post';

export const DEFAULT_POSTS: BlogPost[] = [];

/** Relative age, e.g. "2h ago" / "3d ago". Falls back to the raw value if unparseable. */
export function timeAgo(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}

function normalisePost(raw: any): BlogPost | null {
  if (!raw || typeof raw !== 'object' || !raw.title) return null;
  const title = String(raw.title);
  return {
    id: String(raw.id || slugify(title)),
    slug: String(raw.slug || slugify(title)),
    title,
    excerpt: typeof raw.excerpt === 'string' ? raw.excerpt : '',
    body: typeof raw.body === 'string' ? raw.body : '',
    category: typeof raw.category === 'string' && raw.category ? raw.category : 'Insight',
    image: typeof raw.image === 'string' ? raw.image : '',
    author: typeof raw.author === 'string' && raw.author ? raw.author : 'Conncct',
    readTime: typeof raw.readTime === 'string' ? raw.readTime : '',
    publishedAt: typeof raw.publishedAt === 'string' ? raw.publishedAt : '',
    published: raw.published !== false,
    brand: raw.brand === 'corporate' || raw.brand === 'consulting' ? raw.brand : 'both',
    // Carried through deliberately: the admin editor persists normalised posts,
    // so anything dropped here is erased from the store on the next save.
    // Keys are omitted rather than set to undefined — Firestore rejects undefined.
    ...(typeof raw.sourceUrl === 'string' && raw.sourceUrl ? { sourceUrl: raw.sourceUrl } : {}),
    ...(typeof raw.sourceName === 'string' && raw.sourceName ? { sourceName: raw.sourceName } : {}),
  };
}

/** All posts as stored (admin editor uses this — includes unpublished). */
export function useAllBlogPosts(): { posts: BlogPost[]; loading: boolean } {
  const [posts, setPosts] = useState<BlogPost[]>(DEFAULT_POSTS);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const ref = doc(db, 'blog', 'current');
    return onSnapshot(ref, snap => {
      const d = snap.data() as { posts?: any[] } | undefined;
      const list = Array.isArray(d?.posts) ? d!.posts.map(normalisePost).filter(Boolean) as BlogPost[] : [];
      setPosts(list);
      setLoading(false);
    }, () => setLoading(false));
  }, []);
  return { posts, loading };
}

/** Published posts for THIS brand, newest first — what /blog renders. */
export function useBlogPosts(): { posts: BlogPost[]; loading: boolean } {
  const { posts, loading } = useAllBlogPosts();
  const visible = posts
    .filter(p => p.published && (p.brand === 'both' || p.brand === BRAND))
    .sort((a, b) => (Date.parse(b.publishedAt) || 0) - (Date.parse(a.publishedAt) || 0));
  return { posts: visible, loading };
}

/** Hourly industry headlines for this brand (from the scheduled backend fetch). */
export function useAutoNews(view: 'consulting' | 'accounting' = NEWS_VIEW): AutoNewsItem[] {
  const [items, setItems] = useState<AutoNewsItem[]>([]);
  useEffect(() => {
    const ref = doc(db, 'news', 'auto');
    return onSnapshot(ref, snap => {
      const d = snap.data() as Record<string, any> | undefined;
      const list = Array.isArray(d?.[view]) ? d![view] : [];
      setItems(list.filter((n: any) => n && n.title && n.url));
    }, () => { /* permission/transient errors: render nothing rather than break the page */ });
  }, [view]);
  return items;
}

/**
 * Paragraphs to render for an article body.
 *
 * Drafted articles end with a literal `Source: <publisher> — <url>` line (the
 * writer is told to add it). Both article pages already render that as a proper
 * "Read the original" button, so the raw line is a duplicate — and the URL is
 * often a very long Google News redirect that overflows the card. Strip it when
 * we have the link, keep it when we don't.
 */
export function articleParagraphs(post: Pick<BlogPost, 'body' | 'excerpt' | 'sourceUrl'>): string[] {
  const paras = (post.body || post.excerpt || '')
    .split(/\n{2,}/)
    .map(s => s.trim())
    .filter(Boolean);
  if (!post.sourceUrl) return paras;
  const last = paras[paras.length - 1] || '';
  return /^Source\s*:/i.test(last) ? paras.slice(0, -1) : paras;
}
